import { Parser } from 'expr-eval';
import compact from 'lodash-es/compact';
import entries from 'lodash-es/entries';
import get from 'lodash-es/get';
import groupBy from 'lodash-es/groupBy';
import isNumber from 'lodash-es/isNumber';
import map from 'lodash-es/map';
import set from 'lodash-es/set';
import sum from 'lodash-es/sum';
import uniqBy from 'lodash-es/uniqBy';

import { constants } from '@bedc/constants';
import { assertUnreachable } from '@bedc/core';
import type { types } from '@bedc/types';

import { cutoutUtils } from './cutout';
import { geometryUtils } from './geometryUtils';
import type {
	PackEvent,
	PackMaterial,
	Projection,
	ProjectionEvent,
	ProjectionLayer,
} from './projection';
import { projectionUtils } from './projection';

// Match any UUID version/variant — only structure matters (8-4-4-4-12 hex).
// Avoids breakage as UUID versions evolve (v4 → v7 → future).
const REGEX_LINK_PARAMETER =
	/(parameter:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/g;
const REGEX_LINK_MATERIAL = /(material:[a-z]+)/g;
const REGEX_LINK_MEASUREMENT = /(measurement:[a-z]+)/g;
const REGEX_LINK_DEDUCTION = /(deduction:[a-z]+)/g;

function computeConvertedMeasurements(
	pathLayers: types.wholeHouse.canvas.layer.Path[],
	scale?: number
) {
	const measurements: Record<string, { length: number; area: number }[]> = {};
	let totalLength = 0;
	let totalArea = 0;
	let totalLines = 0;
	let totalCutoutArea = 0;
	let totalCutoutLength = 0;
	let totalCutoutCount = 0;

	const defaultScale = scale || 1;

	// Pure measurement of a single ring, in unscaled pixel units. Code-motion of
	// the former inline `processPath`, with the closure mutations lifted out so a
	// cut-out ring can be measured without polluting the running totals.
	function measureRing(path: types.ui.geometry.Path): {
		length: number;
		area: number;
		lines: number;
	} {
		// If null or undefined, return zeroes
		if (path === null || path === undefined) {
			return { length: 0, area: 0, lines: 0 };
		}

		// Process the path with geometryUtils
		const points = geometryUtils.pathToFlattenedPoints(path);
		if (!points || points.length < 4) {
			return { length: 0, area: 0, lines: 0 };
		}

		// Calculate length
		let length = 0;
		let lines = 0;
		for (let i = 0; i < points.length - 2; i += 2) {
			const p1 = points[i];
			const p2 = points[i + 1];
			const p3 = points[i + 2];
			const p4 = points[i + 3];

			if (p1 !== undefined && p2 !== undefined && p3 !== undefined && p4 !== undefined) {
				length += geometryUtils.calculatePixelDistance([p1, p2, p3, p4]);
				lines = lines + 1;
			}
		}

		// Calculate area
		const area = geometryUtils.isPathClosed(path) ? geometryUtils.calculateArea(points) : 0;

		return { length, area, lines };
	}

	pathLayers.forEach((layer) => {
		const pathMeasurements: { length: number; area: number }[] = [];

		// Handle both cases: array of paths or single path
		const pathsArray = Array.isArray(layer.paths) ? layer.paths : [layer.paths].filter(Boolean);

		// Identity map — same order, same length, same references — when the layer
		// has no cutouts, so an uncut layer measures exactly as it did before.
		const ringSets = cutoutUtils.groupRings(pathsArray, layer.cutouts);

		// `groupRings` deliberately pairs every cut-out with every outer ring so
		// area clipping handles openings that span separate areas. Count and raw
		// perimeter are properties of the persisted cut-outs themselves, however,
		// and must only be accumulated once per layer.
		for (const cutout of layer.cutouts ?? []) {
			totalCutoutLength += measureRing(cutout).length;
			totalCutoutCount += 1;
		}

		ringSets.forEach(({ outer, holes }) => {
			const measured = measureRing(outer);

			// Overlap-safe: two openings that overlap contribute their shared area
			// ONCE, and any part of an opening outside the ring contributes nothing.
			// A plain sum would over-subtract, which under-measures the layer and so
			// under-orders material. Falls back to plain arithmetic — no clipping —
			// whenever the openings are provably disjoint and inside the ring, which
			// is the normalised case and the common one.
			const holeArea = cutoutUtils.netHoleArea(outer, holes);

			const ringArea = Math.max(0, measured.area - holeArea);

			// A cut-out is EXCLUDED FROM THE LAYER'S MEASUREMENTS ENTIRELY — not merely
			// subtracted from area. Its perimeter does not count toward `length` and its
			// segments do not count toward `totalLines` (which becomes `quantity` for the
			// 'each' type), because framing around an opening is its own explicit takeoff
			// and folding it in here would double-count against that.
			//
			// Consequence, intentional and pinned by spec: flagging a drawn area as a
			// cut-out lowers the layer's `length` and `each` count as well as its `area`,
			// since the ring stops being part of the measured layer. A layer that feeds
			// both an area recipe and a length recipe will see both quantities move.
			//
			// Note a layer legitimately mixes open polylines (length only, area 0) with
			// closed rings (length AND area); only closed rings can become cut-outs.
			totalLength += measured.length;
			totalArea += ringArea;
			totalLines = totalLines + measured.lines;

			totalCutoutArea += holeArea;

			pathMeasurements.push({
				length: measured.length * defaultScale,
				area: ringArea * defaultScale * defaultScale,
			});
		});

		measurements[layer.id] = pathMeasurements;
	});

	return {
		measurements,
		totalLines,
		totalLength: totalLength * defaultScale,
		totalArea: totalArea * defaultScale * defaultScale,
		// Display only — deliberately not fed into any formula.
		totalCutoutArea: totalCutoutArea * defaultScale * defaultScale,
		totalCutoutLength: totalCutoutLength * defaultScale,
		totalCutoutCount,
	};
}

/** Metric pitches are held short of vertical: 1/cos is infinite at 90° and negative beyond it. */
const maxMetricPitch = 89;

/** Whether a pitch can be entered: a metric one must be under 90° either way. */
const isValidPitch = (pitch: number, measurementSystem: types.ui.MeasurementSystem): boolean =>
	measurementSystem !== 'metric' || Math.abs(pitch) < 90;

/**
 * A layer's pitch as an angle: degrees in metric, rise over an implicit run of
 * 12 in imperial.
 *
 * @param pitch The pitch as entered on the layer.
 * @param measurementSystem How to read it.
 * @returns The slope angle in radians.
 */
const pitchToRadians = (pitch: number, measurementSystem: types.ui.MeasurementSystem): number =>
	measurementSystem === 'metric'
		? (Math.max(-maxMetricPitch, Math.min(maxMetricPitch, pitch)) * Math.PI) / 180
		: Math.atan2(pitch, 12);

/** Measurement types whose formula runs once per line segment, results summed. */
const perSegmentMeasurementTypes: readonly types.wholeHouse.recipe.measurement.Id[] = [
	'each_length',
	'each_length_and_height',
];

const isPerSegmentMeasurement = (measurementType: types.wholeHouse.recipe.measurement.Id) =>
	perSegmentMeasurementTypes.includes(measurementType);

const areaMeasurementTypes: readonly types.wholeHouse.recipe.measurement.Id[] = [
	'area',
	'area_and_height',
	'inclined_area',
];

/**
 * Whether a recipe of the given measurement type can measure a layer. `each` counts marker
 * points and lines, area types measure closed shapes, every other type measures path
 * lengths; anything else would be added and silently count as 0.
 *
 * @param layer The canvas layer.
 * @param measurementType The recipe's measurement type.
 * @returns True when the layer contributes to the recipe.
 */
const isLayerMeasurableBy = (
	layer: types.wholeHouse.canvas.layer.Type,
	measurementType: types.wholeHouse.recipe.measurement.Id
): boolean => {
	if (layer.isImported) return false;
	if (measurementType === 'each') return true;
	if (layer.type !== 'path') return false;
	if (areaMeasurementTypes.includes(measurementType))
		return layer.paths.some((path) => geometryUtils.isPathClosed(path));
	return true;
};

const REGEX_DEDUCTION_REFERENCE = /\bdeduction[.:]/;

export type FormulaIncompatibility = 'deduction' | 'projection';

const formulaIncompatibilityMessage: Record<FormulaIncompatibility, string> = {
	deduction: 'Deductions are not supported by per-segment measurement types',
	projection: 'Projection formulas need an area measurement type',
};

/** Why a formula cannot be used by a recipe of this measurement type; empty when it can. */
const formulaIncompatibilities = (
	formula: string,
	measurementType: types.wholeHouse.recipe.measurement.Id
): FormulaIncompatibility[] => [
	...(isPerSegmentMeasurement(measurementType) && REGEX_DEDUCTION_REFERENCE.test(formula)
		? (['deduction'] as const)
		: []),
	...(!areaMeasurementTypes.includes(measurementType) &&
	projectionUtils.isProjectionFormula(formula)
		? (['projection'] as const)
		: []),
];

/**
 * Every edge of the path layers, each as its own one-segment layer carrying the source
 * layer's measurement values (height). A closed ring repeats its first point last, so its
 * closing edge is one of the edges; cut-outs live in `cutouts`, never in `paths`, so their
 * edges are excluded, and so are zero-length edges left by a repeated point.
 */
const segmentLayersOf = (
	layers: types.wholeHouse.canvas.layer.Type[]
): types.wholeHouse.canvas.layer.Path[] =>
	layers.flatMap((layer) =>
		layer.type === 'path' && Array.isArray(layer.paths)
			? layer.paths.flatMap((path) =>
					path.flatMap((start, index) => {
						const end = path[index + 1];
						// A repeated point is not a wall.
						return end && (end.x !== start.x || end.y !== start.y)
							? [{ ...layer, paths: [[start, end]], cutouts: [] }]
							: [];
					})
				)
			: []
	);

const getMeasurementValuesV2 = (
	layers: types.wholeHouse.canvas.layer.Type[],
	additionalParameters: Record<string, number>,
	measurementType: types.wholeHouse.recipe.measurement.Id,
	scale: number,
	measurementSystem: types.ui.MeasurementSystem,
	precision?: number
): Record<string, number> => {
	// Initialize all known measurement properties to 0 so formulas never
	// encounter undefined values (which would produce NaN and silently
	// collapse to 0 downstream via `|| 0` guards).
	const values: Record<string, number> = {
		quantity: 0,
		area: 0,
		length: 0,
		height: 0,
		volume: 0,
		pitch: 0,
		...(additionalParameters || {}),
	};

	// Convert mm-per-pixel scale to m-per-pixel when in metric mode
	const effectiveScale = measurementSystem === 'metric' ? scale / 1000 : scale;

	const pathLayers = layers
		.filter(
			(layer): layer is types.wholeHouse.canvas.layer.Path =>
				layer.type === 'path' && Array.isArray(layer.paths)
		)
		.map((layer) => {
			const { totalLength, totalArea } = computeConvertedMeasurements(
				[layer],
				effectiveScale
			);
			return {
				...layer,
				measurementValues: {
					...values,
					...layer.measurementValues,
					length: totalLength,
					area: totalArea,
				},
			};
		});

	// if there is height or pitch then we will have to get the height or pitch value from layers i.e the total will be computed from that
	const product = computeConvertedMeasurements(pathLayers, effectiveScale);

	const real_length = product.totalLength;
	const real_area = product.totalArea;

	switch (measurementType) {
		case 'each':
			values['quantity'] =
				sum([
					sum(
						layers.map((layer) =>
							layer.type === 'marker' && layer.points.length > 0
								? layer.points.length
								: 0
						)
					),
					product.totalLines,
				]) || 0;
			break;

		case 'length':
			values['length'] = real_length;
			values['quantity'] = 1;
			break;

		case 'each_length':
			values['length'] = real_length;
			// 1 inside each per-segment evaluation; the segment count over whole layers.
			values['quantity'] = segmentLayersOf(pathLayers).length;
			break;

		case 'length_and_height':
		case 'each_length_and_height': {
			values['length'] = real_length;
			values['quantity'] =
				measurementType === 'each_length_and_height'
					? segmentLayersOf(pathLayers).length
					: 1;
			const firstLayer = pathLayers[0];
			if (
				pathLayers.length === 1 &&
				firstLayer &&
				firstLayer.measurementValues?.height &&
				firstLayer.measurementValues?.height > 0
			) {
				values['height'] = firstLayer.measurementValues?.height;
			}
			if (pathLayers.length > 0) {
				// area is sum of all areas of paths
				values['area'] = sum(
					pathLayers.map((layer) => {
						return (
							layer.measurementValues?.length *
							(layer.measurementValues?.height || values['height'] || 0)
						);
					})
				);
			}

			break;
		}

		case 'inclined_length': {
			values['quantity'] = 1;
			const firstLayer = pathLayers[0];
			if (pathLayers.length === 1 && firstLayer && firstLayer.measurementValues?.pitch) {
				values['pitch'] = firstLayer.measurementValues?.pitch;
			}

			const inclinedLengthCalc = (pitch: number, length: number) =>
				length / Math.cos(pitchToRadians(pitch, measurementSystem));
			if (pathLayers.length > 0) {
				// area is sum of all areas of paths
				values['length'] = sum(
					pathLayers.map((layer) => {
						return (
							inclinedLengthCalc(
								layer.measurementValues?.pitch || 0,
								layer.measurementValues?.length || 0
							) ?? 0
						);
					})
				);
			} else {
				values['length'] = inclinedLengthCalc(values['pitch'] || 0, real_length);
			}
			break;
		}

		case 'inclined_area': {
			values['length'] = real_length;
			values['quantity'] = 1;
			const firstLayer = pathLayers[0];
			if (pathLayers.length === 1 && firstLayer) {
				values['pitch'] = firstLayer.measurementValues?.pitch || values?.['pitch'] || 0;
			}

			const inclinedAreaCalc = (pitch: number, area: number) =>
				area / Math.cos(pitchToRadians(pitch, measurementSystem));

			if (pathLayers.length > 0) {
				// area is sum of all areas of paths
				values['area'] = sum(
					pathLayers.map((layer) => {
						return (
							inclinedAreaCalc(
								layer.measurementValues?.pitch || 0,
								layer.measurementValues?.area || 0
							) ?? 0
						);
					})
				);
			}
			break;
		}

		case 'area':
			values['length'] = real_length;
			values['area'] = real_area;
			values['quantity'] = 1;
			break;

		case 'area_and_height':
			values['length'] = real_length;
			values['area'] = real_area;

			if (pathLayers.length > 0) {
				const calcVolume = (area: number, height: number) => {
					return area * height;
				};
				// area is sum of all areas of paths
				values['volume'] = sum(
					pathLayers.map((layer) => {
						return calcVolume(
							layer.measurementValues?.area || 0,
							layer.measurementValues?.height || values['height'] || 0
						);
					})
				);
			}
			values['quantity'] = 1;
			break;

		// case 'volume':
		// 	values['length'] = real_length;
		// 	values['area'] = real_area;
		// 	values['volume'] = real_area * values['height'];
		// 	values['quantity'] = 1;
		// 	break;

		default:
			assertUnreachable(measurementType);
			break;
	}

	if (precision) {
		Object.keys(values).forEach((key) => {
			const val = values[key];
			if (isNumber(val) && val !== undefined) {
				values[key] = parseFloat(val.toFixed(precision));
			}
		});
	}

	return values;
};

/**
 * Every segment of the layers with the measurements a per-segment formula sees for it,
 * in drawing order; `index` counts within the segment's own layer.
 */
const perSegmentMeasurements = (
	layers: types.wholeHouse.canvas.layer.Type[],
	additionalParameters: Record<string, number>,
	measurementType: types.wholeHouse.recipe.measurement.Id,
	scale: number,
	measurementSystem: types.ui.MeasurementSystem
): { layerId: string; index: number; measurement: Record<string, number> }[] => {
	const indexInLayer: Record<string, number> = {};
	return segmentLayersOf(layers).map((segment) => {
		const index = indexInLayer[segment.id] ?? 0;
		indexInLayer[segment.id] = index + 1;
		return {
			layerId: segment.id,
			index,
			measurement: getMeasurementValuesV2(
				[segment],
				additionalParameters,
				measurementType,
				scale,
				measurementSystem
			),
		};
	});
};

function extractMeasurementValues(
	measurement: types.wholeHouse.recipe.measurement.MeasurementItem,
	defaultValue = 1
): Record<string, number> {
	const res: Record<string, number> = {};
	const measurementDetails =
		typeof measurement !== 'string' && 'measurementType' in measurement
			? { ...measurement, type: measurement.measurementType }
			: ({
					type: measurement,
					measurementType: measurement,
				} as Partial<types.wholeHouse.recipe.measurement.MeasurementItem> & {
					type: types.wholeHouse.recipe.measurement.Id;
				});

	const measurementType = measurementDetails.measurementType;
	if (!measurementType) {
		return res;
	}
	switch (measurementType) {
		case 'each':
			res['quantity'] = measurementDetails?.quantity || defaultValue;
			break;
		case 'length':
			res['length'] = measurementDetails?.length || defaultValue;
			break;
		case 'length_and_height':
			res['length'] = measurementDetails?.length || defaultValue;
			res['height'] = measurementDetails?.height || defaultValue;
			res['area'] =
				measurementDetails?.length && measurementDetails?.height
					? measurementDetails?.length * measurementDetails?.height
					: defaultValue;
			break;
		case 'area':
			res['length'] = measurementDetails?.length || defaultValue;
			res['area'] = measurementDetails?.area || defaultValue;
			break;
		case 'area_and_height':
			res['area'] = measurementDetails?.area || defaultValue;
			res['height'] = measurementDetails?.height || defaultValue;
			res['volume'] =
				measurementDetails?.area && measurementDetails?.height
					? measurementDetails?.area * measurementDetails?.height
					: defaultValue;
			//res['length'] = measurementDetails?.length || defaultValue;
			break;
		case 'inclined_area':
			res['length'] = measurementDetails?.length || defaultValue;
			res['area'] = measurementDetails?.area || defaultValue;
			res['pitch'] = measurementDetails?.pitch || defaultValue;
			break;
		// case 'volume':
		// 	res['volume'] = measurementDetails?.volume || defaultValue;
		// 	break;
		default: {
			assertUnreachable(measurementType, 'extractMeasuermentValues');
		}
	}

	return res;
}

function getDrawingPropertiesByMeasurementType(
	measurementType: types.wholeHouse.recipe.measurement.Id
): types.wholeHouse.formula.measurementProperty.Id[] {
	switch (measurementType) {
		case 'each':
			return ['quantity'];
		case 'length':
			return ['length'];
		case 'length_and_height':
			return ['length', 'height', 'area'];
		case 'area':
			return ['length', 'area'];
		case 'area_and_height':
			return ['length', 'area', 'height', 'volume'];
		case 'inclined_area':
			// should introduce inclined_area instead?
			return ['length', 'pitch', 'area'];
		case 'inclined_length':
			return ['length', 'pitch'];
		case 'each_length':
			return ['length', 'quantity'];
		case 'each_length_and_height':
			return ['length', 'height', 'area', 'quantity'];
		default: {
			return assertUnreachable(measurementType, 'getDrawingPropertiesByMeasurementType');
		}
	}
}

function applyPriceUnitIncrement(initialValue: number, increment: number, precision = 4): number {
	return (
		Math.round(increment * Math.ceil(initialValue / increment) * Math.pow(10, precision)) /
		Math.pow(10, precision)
	);
}

const parseQuantity = (value: number): number => {
	return Math.round(value * 100) / 100;
};

function applyWastage(initialValue: number, wastage: number): number {
	return parseQuantity(initialValue * (1 + wastage));
}

/**
 * One ingredient's contribution, honouring a user override.
 *
 * - An override REPLACES the post-wastage value; wastage is not applied on top.
 * - Deductions are overridable on the same terms.
 */
function ingredientQuantityWithWastage(ingredient: {
	calculated: number;
	wastage: number;
	isDeduction: boolean;
	quantityOverride?: number;
}): number {
	const quantity =
		ingredient.quantityOverride !== undefined
			? ingredient.quantityOverride
			: ingredient.isDeduction
				? ingredient.calculated
				: wohUtils.applyWastage(ingredient.calculated, ingredient.wastage);

	// A quantity is material to deliver, so zero is its floor whatever produced it: a formula can
	// evaluate negative on unusual geometry, and an override is typed by hand. Neither may put a
	// negative on the BoM. A deduction carries a positive magnitude and is subtracted by the
	// caller, so it floors on the same terms. A non-finite result is a broken formula rather than
	// a quantity, and reads as nothing to order.
	return Number.isFinite(quantity) ? Math.max(0, quantity) : 0;
}

function applyQuantityAdjustments(
	initialQuantity: number,
	priceUnit: types.wholeHouse.material.priceUnit.PriceUnitItem
): number {
	if (initialQuantity === 0) {
		return 0;
	}
	let adjustedQuantity = initialQuantity;

	if ('packQty' in priceUnit && priceUnit.packQty > 1) {
		adjustedQuantity /= priceUnit.packQty;
	}

	if (priceUnit.min !== null) {
		adjustedQuantity = Math.max(adjustedQuantity, priceUnit.min);
	}

	if (priceUnit.inc !== null) {
		adjustedQuantity = applyPriceUnitIncrement(adjustedQuantity, priceUnit.inc);
	}

	return adjustedQuantity;
}

/**
 * Processes quantity calculation for a single ingredient with drawing and deduction layers
 * Applies deductions, evaluates formulas, and returns the calculated quantity with metadata
 * @param ingredient The ingredient with formula details and material usage information
 * @param canvasRecipe The canvas recipe containing measurement type and additional parameters
 * @param drawingLayers The layers representing the drawing area to measure
 * @param deductionLayers The layers representing areas to deduct from the drawing
 * @param materialValues Pre-calculated material parameter values
 * @param measurementProperties Pre-calculated measurement properties from additional parameters
 * @param additionalParameterValues Pre-calculated additional parameter values
 * @param measurementSystem The measurement system being used
 * @param canvas The canvas data containing scale and other information
 * @param ingredientIndex The index of the ingredient in the selected ingredients array
 * @param selectedIngredients The array of selected ingredients for the recipe
 * @returns Quantity calculation result with formula values and metadata
 */
/** A projection reported out of one ingredient evaluation, tied to its layer. */
type LayerProjection = {
	/** The canvas layer the geometry came from, when attributable. */
	layerId?: string;
	/** LINE-SPACE projection; `projectionUtils.projectionToDrawingLines` renders it. */
	projection: Projection;
	/** On-centre spacing in drawing units. */
	spacing: number;
	/** The line angle the projection was computed at. */
	angleDegree: number;
	/** True length per plan length, for measuring the segments (absent = flat). */
	slopeFactor?: number;
};

/** A projection the BOM evaluation computed, attributed to its recipe context. */
export type CanvasProjection = LayerProjection & {
	recipeId: types.wholeHouse.canvas.recipe.Id;
	/** The derived-layer toggle key — the ingredient's name (may be empty). */
	ingredientName: string;
	ingredientIndex: number;
};

/**
 * Sticks grouped by the pieces cut from them — the cutting plan the yard
 * follows. Most common pattern first, then the one holding the longest piece.
 *
 * @param sticks The sticks cut from one stock.
 * @param stockLength That stock's length.
 * @returns One entry per distinct pattern.
 */
const cuttingPatternsOf = (
	sticks: { pieces: number[] }[],
	stockLength: number
): types.wholeHouse.metric.ProjectionBreakdown['stocks'][number]['patterns'] => {
	const byPattern = new Map<string, { pieces: number[]; sticks: number; offcutEach: number }>();
	for (const stick of sticks) {
		const pieces = [...stick.pieces].sort((left, right) => right - left);
		const key = pieces.join('|');
		const pattern = byPattern.get(key) ?? {
			pieces,
			sticks: 0,
			offcutEach: Math.max(0, stockLength - sum(pieces)),
		};
		pattern.sticks += 1;
		byPattern.set(key, pattern);
	}

	return [...byPattern.values()].sort(
		(left, right) =>
			right.sticks - left.sticks || (right.pieces[0] ?? 0) - (left.pieces[0] ?? 0)
	);
};

/**
 * The breakdown of one projection ingredient: what the paved road projected
 * and packed, with each selected material's share of the cut list.
 *
 * @param pack The pack the formula ran (the last call, when it ran several).
 * @param events Each layer's projection from that evaluation.
 * @param layers The recipe's path layers, index-aligned with the projection's.
 * @param materials The selected materials, in allocation order.
 */
const toProjectionBreakdown = ({
	pack,
	events,
	layers,
	materials,
	scale,
	measurementSystem,
}: {
	pack: PackEvent;
	events: ProjectionEvent[];
	layers: types.wholeHouse.metric.ProjectionBreakdown['layers'];
	materials: (PackMaterial & { name: string; sku: string })[];
	scale: number;
	measurementSystem: types.ui.MeasurementSystem;
}): types.wholeHouse.metric.ProjectionBreakdown => {
	const planLength = sum(
		projectionUtils.segmentLengths(
			events.map((event) => event.projection),
			scale,
			measurementSystem
		)
	);

	// Duplicate lengths: the first material of a length owns its sticks, as in the pack.
	const owner = new Map<number, number>();
	materials.forEach((material, index) => {
		if (!owner.has(material.length)) owner.set(material.length, index);
	});

	return {
		spacing: pack.spacing,
		angleDegree: pack.angleDegree,
		maxCutsPerStick: pack.maxCutsPerStick,
		layers,
		linesPlaced: sum(events.map((event) => event.projection.linesPlaced)),
		linesSplit: sum(events.map((event) => event.projection.linesSplit)),
		planLength,
		totalLength: sum(pack.pieces),
		pieceCount: pack.pieces.length,
		stocks: materials.map((material, index) => {
			const sticks =
				owner.get(material.length) === index
					? pack.sticks.filter((stick) => stick.stock === material.length)
					: [];
			const counts = new Map<number, number>();
			for (const piece of sticks.flatMap((stick) => stick.pieces)) {
				counts.set(piece, (counts.get(piece) ?? 0) + 1);
			}
			const cut = sum(sticks.flatMap((stick) => stick.pieces));

			return {
				materialName: material.name,
				sku: material.sku,
				length: material.length,
				sticks: sticks.length,
				pieces: [...counts.entries()]
					.sort(([left], [right]) => right - left)
					.map(([length, count]) => ({ length, count })),
				offcut: Math.max(0, sticks.length * material.length - cut),
				patterns: cuttingPatternsOf(sticks, material.length),
			};
		}),
		unservable: (({ count, longest }) => ({ count, longest }))(
			projectionUtils.unservableSegments(
				pack.pieces,
				materials.map((material) => material.length)
			)
		),
	};
};

function calculateQuantityFromLayers(
	ingredient: types.wholeHouse.canvas.recipe.ingredient.Type,
	canvasRecipe: types.wholeHouse.canvas.recipe.Type,
	drawingLayers: types.wholeHouse.canvas.layer.Type[],
	deductionLayers: types.wholeHouse.canvas.layer.Type[],
	materialValues: Record<string, number>,
	measurementProperties: Record<string, number>,
	additionalParameterValues: Record<string, number>,
	measurementSystem: types.ui.MeasurementSystem,
	canvas: Pick<types.wholeHouse.canvas.DetailsItem, 'id' | 'layers' | 'scale' | 'recipes'>,
	ingredientIndex: number,
	materialIndex: number,
	selectedIngredients: types.wholeHouse.canvas.recipe.ingredient.Type[],
	onProject?: (event: LayerProjection) => void
): types.wholeHouse.metric.Quantity | types.wholeHouse.metric.Quantity[] {
	// currently doesn't support drawing.quantity other than for each
	const drawingProperties = getMeasurementValuesV2(
		drawingLayers,
		measurementProperties,
		canvasRecipe.measurementType,
		canvas.scale,
		measurementSystem
	);

	// deduction properties
	const deductionProperties = getMeasurementValuesV2(
		deductionLayers,
		measurementProperties,
		canvasRecipe.measurementType,
		canvas.scale,
		measurementSystem
	);

	if (deductionLayers.length > 0) {
		const deductionMeasurementTypes = getDrawingPropertiesByMeasurementType(
			canvasRecipe.measurementType
		).filter((type) => type !== 'quantity' && type !== 'height' && type !== 'pitch');
		deductionMeasurementTypes.forEach((measurementType) => {
			if (measurementType in drawingProperties && measurementType in deductionProperties) {
				const drawingVal = drawingProperties[measurementType];
				const deductionVal = deductionProperties[measurementType];
				if (drawingVal !== undefined && deductionVal !== undefined) {
					drawingProperties[measurementType] = drawingVal - deductionVal;
				}
			}
		});
	}

	const formulaParameterValues: Record<string, Record<string, number>> = {
		material: materialValues,
		parameter: additionalParameterValues,
		deduction: deductionLayers.length > 0 ? deductionProperties : {},
		measurement: drawingProperties,
		...additionalParameterValues,
	};

	// Log with context so it's clear this runs once per selected ingredient
	// (selectedIngredients.forEach). Seeing multiple entries usually means
	// there are multiple selected ingredients or recipes — include ids to help debug.
	// ! We shouldn't be keeping console logs active post-release.
	// console.log(
	// 	'formulaParameterValues',
	// 	JSON.stringify({
	// 		canvasRecipeId: canvasRecipe.id,
	// 		recipeName: canvasRecipe.name,
	// 		materialId: ingredient.materialUsageDetails.materialId,
	// 		materialSku: ingredient.materialUsageDetails.sku,
	// 		formulaParameterValues,
	// 	})
	// );

	let parametersInFormula = ingredient.formulaDetails.parameters;

	// extract formula values based on the parameters used in the formula
	const parametersExtractedFromFormula = extractVariablesFromFormula(
		ingredient.formulaDetails.formula
	);

	// A projection formula's scope bindings are injected at evaluation time —
	// expected to be absent from the recipe's parameter list, so the missing-
	// parameter diff must not report them on every BOM recalculation.
	const expectedBindings = projectionUtils.isProjectionFormula(ingredient.formulaDetails.formula)
		? projectionUtils.projectionBindingNames
		: [];

	if (Array.isArray(parametersExtractedFromFormula)) {
		const missingFormulaParameters = parametersExtractedFromFormula.filter(
			(param) => !parametersInFormula.includes(param) && !expectedBindings.includes(param)
		);
		if (missingFormulaParameters.length > 0) {
			console.warn(
				`Missing the following parameters in formula details: ${missingFormulaParameters.join(', ')}. Adding them from formula extraction.`,
				{
					canvasId: canvas.id,
					canvasRecipeId: canvasRecipe.id,
					recipeName: canvasRecipe.name,
					materialId: ingredient.materialUsageDetails.materialId,
					materialSku: ingredient.materialUsageDetails.sku,
					ingredientIndex,
					materialIndex,
					formulaParameterValues,
					formula: ingredient.formulaDetails.formula,
					formulaParametersInDetails: ingredient.formulaDetails.parameters,
					missingParameters: missingFormulaParameters,
				}
			);

			parametersInFormula = parametersExtractedFromFormula;
		}
	} else {
		console.warn(
			`Error: returving variables extractVariablesFromFormula ${parametersExtractedFromFormula.error} ${JSON.stringify(
				{
					canvasId: canvas.id,
					canvasRecipeId: canvasRecipe.id,
					recipeName: canvasRecipe.name,
					materialId: ingredient.materialUsageDetails.materialId,
					materialSku: ingredient.materialUsageDetails.sku,
					ingredientIndex,
					materialIndex,
					formulaParameterValues,
					formula: ingredient.formulaDetails.formula,
				}
			)}`
		);
	}

	const valuesInFormula = (parameterValues: Record<string, Record<string, number>>) =>
		compact(
			// TODO: fix ingredient.formulaDetails.parameters with script
			parametersInFormula?.map((k: string): [string, number] | null => {
				if (k.includes('.')) {
					const [key, subkey] = k.split('.');
					if (key && subkey) {
						const parent = get(parameterValues, key);
						if (parent) {
							const val = parent[subkey];
							if (val !== undefined) {
								// change the precision to 2
								const precision = 2;

								return [
									k,
									Math.round(val * Math.pow(10, precision)) /
										Math.pow(10, precision),
								];
							}
						}
					}
				}
				return null;
			})
		);

	const toFormulaObj = (values: Array<[string, number]>) => {
		const obj: Record<string, Record<string, number>> = {};
		values.forEach(([k, v]) => {
			set(obj, k, v);
		});
		return obj;
	};

	const formulaValues = valuesInFormula(formulaParameterValues);
	const formulaObj = toFormulaObj(formulaValues);

	/** The allocation's index alignment; only the projection path widens this. */
	let selectedMaterials: types.wholeHouse.canvas.recipe.ingredient.Type[] = [ingredient];

	/** Geometry the paved road reports during evaluation — the pack context. */
	const projectionEvents: ProjectionEvent[] = [];
	/** The pack the paved road reports, and what the breakdown needs to explain it. */
	let packEvent: PackEvent | undefined;
	let breakdownContext:
		| Pick<Parameters<typeof toProjectionBreakdown>[0], 'layers' | 'materials'>
		| undefined;
	const effectiveScale =
		measurementSystem === 'metric' ? (canvas.scale ?? 0) / 1000 : (canvas.scale ?? 0);

	// The projection binding (PR #1859). Attached AFTER the numeric flatten —
	// the flatten exists for display strings and rounds to 2 dp, which must
	// never touch geometry or functions — and gated on the paved-road token so
	// every legacy formula evaluates byte-identically.
	//
	// ONE binding, not three: the layers and materials go into the factory, not
	// into scope. The formula field cannot choose either of them, so naming
	// them bought nothing and only made `layers+materials` expressible.
	if (projectionUtils.isProjectionFormula(ingredient.formulaDetails.formula)) {
		// Save rejects this pairing; a recipe stored or copied past that check gets no line
		// rather than one packed from plan lengths.
		const incompatibilities = formulaIncompatibilities(
			ingredient.formulaDetails.formula,
			canvasRecipe.measurementType
		);

		if (incompatibilities.includes('projection')) {
			console.warn(formulaIncompatibilityMessage.projection, {
				canvasId: canvas.id,
				canvasRecipeId: canvasRecipe.id,
				recipeName: canvasRecipe.name,
				measurementType: canvasRecipe.measurementType,
			});

			return [];
		}

		// The recipe's assigned layers, in their persisted shape. `pathLayers`
		// stays index-aligned with the view so a collected projection can be
		// attributed back to its canvas layer by reference.
		const pathLayers = drawingLayers.filter(
			(layer): layer is types.wholeHouse.canvas.layer.Path => layer.type === 'path'
		);
		// On an inclined recipe the lines are taken to run up the slope, so each
		// piece is its plan length × sec(pitch); spacing is unaffected. Pitch
		// resolves as the measurements do: the layer's, else the recipe's.
		const isInclined = canvasRecipe.measurementType === 'inclined_area';
		const slopeFactorOf = (layer: types.wholeHouse.canvas.layer.Path) => {
			const pitch = layer.measurementValues?.pitch ?? measurementProperties['pitch'] ?? 0;
			return 1 / Math.cos(pitchToRadians(pitch, measurementSystem));
		};
		const layersView: ProjectionLayer[] = pathLayers.map((layer) => ({
			paths: layer.paths,
			cutouts: layer.cutouts,
			slopeFactor: isInclined ? slopeFactorOf(layer) : undefined,
		}));

		// The ingredient's SELECTED materials as they exist on the recipe.
		// `ingredient` is the same object reference `.find(isSelected)` returned,
		// so its option resolves by identity. This array's ORDER is the
		// allocation's index alignment — the fan-out below reuses it.
		const option = canvasRecipe.ingredientOptions.find((candidate) =>
			candidate.materials.includes(ingredient)
		);
		selectedMaterials = option ? option.materials.filter((m) => m.isSelected) : [ingredient];
		const materialsView: (PackMaterial & { name: string; sku: string })[] =
			selectedMaterials.map((material) => {
				const values = getMaterialParameterValues({
					propertyValues: material.materialUsageDetails.propertyValues,
					propertyTypes: material.materialUsageDetails.propertyTypes,
					wastage: material.materialUsageDetails.wastage,
				});

				return {
					...values,
					// A material with no `length` property type cannot serve a piece.
					// 0 says that in the pack's own terms — every piece exceeds it, so
					// the allocation comes back unservable and live validation names
					// the catalogue gap, rather than NaN-comparing its way there.
					length: values['length'] ?? 0,
					name: material.materialUsageDetails.materialName,
					sku: material.materialUsageDetails.sku,
				};
			});

		const bindings = formulaObj as Record<string, unknown>;
		bindings['packProjectedLineSegments'] = projectionUtils.makePackProjectedLineSegments(
			effectiveScale,
			measurementSystem,
			layersView,
			materialsView,
			(event) => {
				projectionEvents.push(event);
				onProject?.({
					// Reference match against the binding, so geometry computed
					// from anything other than the recipe's own layers simply
					// reports no layer id.
					layerId: pathLayers[layersView.indexOf(event.layer)]?.id,
					projection: event.projection,
					spacing: event.spacing,
					angleDegree: event.angleDegree,
					slopeFactor: event.layer.slopeFactor,
				});
			},
			(event) => {
				packEvent = event;
			}
		);
		breakdownContext = {
			layers: pathLayers.map((layer, index) => ({
				id: layer.id,
				name: layer.name,
				slopeFactor: layersView[index]?.slopeFactor,
			})),
			materials: materialsView,
		};
	}

	/** What each segment evaluated to; set by a per-segment evaluation only. */
	let segmentBreakdown: types.wholeHouse.metric.SegmentBreakdown[] | undefined;

	/** The formula once per segment, summed; each run sees that segment's measurements. */
	const evaluatePerSegment = () => {
		const segments = perSegmentMeasurements(
			drawingLayers,
			measurementProperties,
			canvasRecipe.measurementType,
			canvas.scale,
			measurementSystem
		);
		formulaValues.push(['segments', segments.length]);
		// Projection formulas are area-only; their bindings are never attached per segment.
		if (projectionUtils.isProjectionFormula(ingredient.formulaDetails.formula)) {
			return 0;
		}
		const withHeight = canvasRecipe.measurementType === 'each_length_and_height';
		segmentBreakdown = segments.map(({ layerId, index, measurement }) => {
			const value = evaluateFormula(
				ingredient.formulaDetails.formula,
				toFormulaObj(valuesInFormula({ ...formulaParameterValues, measurement }))
			);
			return {
				layerId,
				index,
				length: measurement['length'] ?? 0,
				...(withHeight ? { height: measurement['height'] ?? 0 } : {}),
				quantity: isNumber(value) ? value : 0,
			};
		});
		return sum(segmentBreakdown.map((segment) => segment.quantity));
	};

	try {
		const quantity = isPerSegmentMeasurement(canvasRecipe.measurementType)
			? evaluatePerSegment()
			: evaluateFormula(ingredient.formulaDetails.formula, formulaObj);

		/**
		 * One result for one material. The formula fields come from the
		 * ingredient (one formula per ingredient); the material identity and
		 * pricing come from the material the quantity belongs to.
		 */
		const makeResult = (
			material: types.wholeHouse.canvas.recipe.ingredient.Type,
			rawQuantity: number,
			resultMaterialIndex: number,
			projection?: types.wholeHouse.metric.ProjectionBreakdown
		) => ({
			...(projection ? { projection } : {}),
			...(segmentBreakdown ? { segments: segmentBreakdown } : {}),
			canvasRecipeId: canvasRecipe.id,
			recipeName: canvasRecipe.name,
			materialId: material.materialUsageDetails.materialId,
			materialSku: material.materialUsageDetails.sku,
			materialName: material.materialUsageDetails.materialName,
			materialQuantity: isFinite(rawQuantity) ? parseQuantity(rawQuantity) : 0,

			formula: ingredient.formulaDetails.formula,
			formulaValues: formulaValues as Array<[string, number]>,
			parameterValueString: (formulaValues as Array<[string, number]>)
				.map(([k, v]) => `${k}: ${v}`)
				.join(', '),
			materialPriceUnit: material.materialUsageDetails.priceUnit.priceUnit,

			materialTemplateUsageId: material.materialTemplateUsageId,
			formulaUsageId: material.formulaUsageId,
			formulaId: ingredient.formulaDetails.formulaId,
			formulaName: ingredient.formulaDetails.name,
			priceUnit: material.materialUsageDetails.priceUnit,
			priceUnitRef: material.materialUsageDetails.priceUnitRef,
			wastage: material.materialUsageDetails.wastage,
			quantityOverride: material.quantityOverride,

			usageId: material.materialUsageDetails.usageId,
			usageName: material.materialUsageDetails.usageName,
			layers: drawingLayers,
			deductionLayers: deductionLayers,
			ingredientIndex: ingredientIndex,
			materialIndex: resultMaterialIndex,
			isDeduction: false,
			canvasRecipe: {
				id: canvasRecipe.id,
				refrencedRecipeId: canvasRecipe.referencedRecipeId,
				name: canvasRecipe.name,
				measurementType: canvasRecipe.measurementType,
				additionalParameterValues: canvasRecipe.additionalParameterValues,
				ingredients: selectedIngredients,
				adding: false,
				layerIds: canvasRecipe.layerIds,
				deductionLayerIds: canvasRecipe.deductionLayerIds,
			},
		});

		// A list is the multi-material allocation, index-aligned with the
		// selected materials: one BOM line per material with quantity > 0
		// (zero-quantity materials get no line, per design; an all-zero
		// sanitized-unservable allocation emits nothing and live validation
		// carries the message). Array shape is ALLOWED only when the formula is
		// known to return one — the same predicate that gated the bindings — so
		// an accidental array from any other formula falls to the scalar guard.
		if (
			Array.isArray(quantity) &&
			projectionUtils.isProjectionFormula(ingredient.formulaDetails.formula)
		) {
			// Pack context for the breakdown: what the formula projected before
			// packing. Appended to the substituted values so every consumer of
			// formulaValues (previews, BOM breakdown, copy text) shows it.
			if (projectionEvents.length > 0) {
				const lengths = projectionUtils.slopedSegmentLengths(
					projectionEvents.map((event) => ({
						projection: event.projection,
						slopeFactor: event.layer.slopeFactor,
					})),
					effectiveScale,
					measurementSystem
				);
				(formulaValues as Array<[string, number]>).push(
					[
						'projected lines',
						projectionEvents.reduce(
							(sum, event) => sum + event.projection.linesPlaced,
							0
						),
					],
					['segments', lengths.length],
					[
						measurementSystem === 'imperial' ? 'total length (ft)' : 'total length (m)',
						parseQuantity(lengths.reduce((sum, length) => sum + length, 0)),
					]
				);
			}

			const projection =
				packEvent && breakdownContext
					? toProjectionBreakdown({
							pack: packEvent,
							events: projectionEvents,
							...breakdownContext,
							scale: effectiveScale,
							measurementSystem,
						})
					: undefined;

			return (
				selectedMaterials
					.map((material, index) =>
						makeResult(material, quantity[index] ?? 0, index, projection)
					)
					// Zero-quantity materials get no line, except where a user typed a quantity
					// for one: the pack served it nothing, but the override is the whole point
					// and dropping the line would silently discard what they entered.
					.filter(
						(result) =>
							result.materialQuantity > 0 || result.quantityOverride !== undefined
					)
			);
		}

		return makeResult(ingredient, isNumber(quantity) ? quantity : 0, materialIndex);
	} catch (e) {
		console.error('Error evaluating formula', {
			error: e,
			canvasId: canvas.id,
			canvasRecipeId: canvasRecipe.id,
			recipeName: canvasRecipe.name,
			materialId: ingredient.materialUsageDetails.materialId,
			materialSku: ingredient.materialUsageDetails.sku,
			ingredientIndex,
			materialIndex,
			formulaParameterValues,

			formula: ingredient.formulaDetails.formula,
			formulaObj,
		});
		throw e;
	}
}

/**
 * Calculates quantities for a canvas based on its layers, recipes, and measurement system
 * @param canvas The canvas data containing layers, scale, and recipes
 * @param measurementSystem The measurement system to use for calculations
 * @param onProject Observer for projection geometry computed along the way
 *   (drawing passes only — deduction passes subtract, they are not framing).
 * @returns Array of calculated quantities with associated metadata
 */
/**
 * The selected material of every ingredient slot, paired with the slot it came from.
 *
 * The slot travels with its material rather than being looked up by position afterwards. A
 * filtered list of selections indexed against the unfiltered `ingredientOptions` misaligns the
 * moment any slot has nothing selected, attributing each later ingredient to the preceding slot
 * and yielding `materialIndex: -1`.
 *
 * Slots with no selection are skipped, as before. `findIndex` selects the same first-selected
 * material `find` did; a multi-material ingredient is still represented by its first, with the
 * formula allocating across the rest.
 */
function selectedIngredientsOf(canvasRecipe: types.wholeHouse.canvas.recipe.Type) {
	return compact(
		canvasRecipe.ingredientOptions.map((ingredientOption, ingIndex) => {
			const materialIndex = ingredientOption.materials.findIndex(
				(material) => material.isSelected
			);
			if (materialIndex < 0) {
				return null;
			}

			const ingredient = ingredientOption.materials[materialIndex];
			if (!ingredient) {
				return null;
			}

			return { ingredient, ingredientOption, ingIndex, materialIndex };
		})
	);
}

const calculateQuantityForCanvas = (
	canvas: Pick<types.wholeHouse.canvas.DetailsItem, 'id' | 'layers' | 'scale' | 'recipes'>,
	measurementSystem: types.ui.MeasurementSystem,
	onProject?: (event: CanvasProjection) => void
): types.wholeHouse.metric.Quantity[] => {
	if (
		!canvas.recipes ||
		canvas.recipes.length === 0 ||
		!canvas.layers ||
		canvas.layers.length === 0
	) {
		return [];
	}

	const res: types.wholeHouse.metric.Quantity[] = [];

	for (const canvasRecipe of canvas.recipes) {
		const selectedIngredientSlots = selectedIngredientsOf(canvasRecipe);
		// Unchanged in order and contents — the downstream quantity calculation still receives the
		// flat one-material-per-slot list it always did.
		const selectedIngredients = selectedIngredientSlots.map((slot) => slot.ingredient);

		selectedIngredientSlots.forEach((slot) => {
			const { ingredient, ingredientOption, ingIndex, materialIndex } = slot;

			const drawingLayers = canvas.layers.filter((layer) =>
				canvasRecipe.layerIds?.includes(layer.id)
			);
			// if there are layers associated with this recipe and is not available in canvas then skip
			// Some bug when deleteing layers?
			if (
				canvasRecipe.layerIds &&
				canvasRecipe.layerIds.length > 0 &&
				drawingLayers.length === 0
			) {
				return;
			}

			// Per-segment measurement types take no deductions.
			const perSegment = isPerSegmentMeasurement(canvasRecipe.measurementType);
			const deductionLayers = perSegment
				? []
				: canvas.layers.filter((layer) =>
						canvasRecipe.deductionLayerIds?.includes(layer.id)
					);

			// if there are deduction layers associated with this recipe and is not available in canvas then skip
			// Some bug when deleteing layers?
			if (
				!perSegment &&
				canvasRecipe.deductionLayerIds &&
				canvasRecipe.deductionLayerIds.length > 0 &&
				deductionLayers.length === 0
			) {
				return;
			}

			/** Attribute collected geometry to this recipe and ingredient. */
			const collectProjection =
				onProject &&
				((event: LayerProjection) =>
					onProject({
						...event,
						recipeId: canvasRecipe.id,
						ingredientIndex: ingIndex,
						ingredientName: ingredientOption.ingredientName,
					}));

			// get material measurement values
			const materialValues = getMaterialParameterValues({
				propertyValues: ingredient.materialUsageDetails.propertyValues,
				propertyTypes: ingredient.materialUsageDetails.propertyTypes,
				wastage: ingredient.materialUsageDetails.wastage,
			});

			const additionalParameterValues = getAdditionalParameterValues(
				canvasRecipe.additionalParameterValues
					.filter((param) => param.parameterRef.startsWith('parameter.'))
					.map((param) => ({
						parameterRef: param.parameterRef,
						defaultValue: param.value,
					}))
			);

			// get additional parameter values where the parameterId contains measurement.
			const measurementProperties = getAdditionalParameterValues(
				canvasRecipe.additionalParameterValues
					.filter((param) => param.parameterRef.startsWith('measurement.'))
					.map((param) => ({
						parameterRef: param.parameterRef.replace('measurement.', ''),
						defaultValue: param.value,
					}))
			);

			const measurementTypesInFormulas = ingredient.formulaDetails.parameters;
			const requiresIndividualLayerComputation = measurementTypesInFormulas.some((param) =>
				['measurement.height', 'measurement.pitch'].includes(param)
			);

			// get drawing measurement values
			if (requiresIndividualLayerComputation) {
				// Process each layer individually to support measurement.height and measurement.pitch
				for (const layer of drawingLayers) {
					// For individual layer computation, process with just that layer
					const quantity = calculateQuantityFromLayers(
						ingredient,
						canvasRecipe,
						[layer], // Process one layer at a time
						[], // deduction not supported in this mode currently
						materialValues,
						measurementProperties,
						additionalParameterValues,
						measurementSystem,
						canvas,
						ingIndex,
						materialIndex,
						selectedIngredients,
						collectProjection
					);
					res.push(...(Array.isArray(quantity) ? quantity : [quantity]));
				}

				for (const layer of deductionLayers) {
					// For individual layer computation, process with just that layer
					const quantity = calculateQuantityFromLayers(
						ingredient,
						canvasRecipe,
						[layer], // Process one layer at a time
						[], // deduction not supported in this mode currently
						materialValues,
						measurementProperties,
						additionalParameterValues,
						measurementSystem,
						canvas,
						ingIndex,
						materialIndex,
						selectedIngredients
					);
					for (const result of Array.isArray(quantity) ? quantity : [quantity]) {
						res.push({
							...result,
							// mark as deduction
							isDeduction: true,
						});
					}
				}
			} else {
				// Use the extracted function to calculate quantity
				const quantity = calculateQuantityFromLayers(
					ingredient,
					canvasRecipe,
					drawingLayers,
					deductionLayers,
					materialValues,
					measurementProperties,
					additionalParameterValues,
					measurementSystem,
					canvas,
					ingIndex,
					materialIndex,
					selectedIngredients,
					collectProjection
				);
				res.push(...(Array.isArray(quantity) ? quantity : [quantity]));
			}
		});
	}

	return res;
};

/**
 * The projections a canvas's BOM evaluation computes, without the BOM.
 *
 * Runs the exact evaluation `calculateQuantityForCanvas` runs and keeps only
 * the collected geometry, so the derived "projected lines" layer can never
 * disagree with the BOM — same formulas, same bindings, same clipping.
 *
 * @param canvas The canvas whose recipes to evaluate.
 * @param measurementSystem The measurement system the BOM evaluates under.
 * @returns Every projection computed by a drawing pass, in evaluation order.
 */
const collectCanvasProjections = (
	canvas: Pick<types.wholeHouse.canvas.DetailsItem, 'id' | 'layers' | 'scale' | 'recipes'>,
	measurementSystem: types.ui.MeasurementSystem
): CanvasProjection[] => {
	const projections: CanvasProjection[] = [];
	calculateQuantityForCanvas(canvas, measurementSystem, (event) => projections.push(event));

	return projections;
};

function getMaterialPropertiesUsedByFormula(
	propertyType: types.wholeHouse.material.propertyType.Id
): types.wholeHouse.formula.materialProperty.Id {
	switch (propertyType) {
		case 'each':
			return 'quantity';
		case 'length':
		case 'area':
		case 'volume':
		case 'height':
		case 'coverage':
			return propertyType;
		default:
			return assertUnreachable(propertyType, 'getMaterialFormulaProperties');
	}
}

function getMaterialParameterValues(materialUsage: {
	propertyValues: types.wholeHouse.material.propertyType.PropertyItem;
	propertyTypes: types.wholeHouse.material.propertyType.Id[];
	wastage: number;
}): Record<string, number> {
	const parameterValues: Record<string, number> = {};

	const propertytypes = materialUsage.propertyTypes;

	if (propertytypes.length === 0) {
		return parameterValues;
	}

	for (const propertyType of propertytypes) {
		if (propertyType === 'each') {
			parameterValues['quantity'] = materialUsage.propertyValues.each ?? 1;
		} else {
			const val = materialUsage.propertyValues[propertyType];
			parameterValues[propertyType] = val ?? 0;
		}
	}

	if ('wastage' in materialUsage) {
		parameterValues['wastage'] = materialUsage.wastage;
	}

	return parameterValues;
}

function getAdditionalParameterValues(
	parameters: Pick<
		types.wholeHouse.additionalParameter.ListItem,
		'defaultValue' | 'parameterRef'
	>[] = []
): Record<string, number> {
	const parameterValues: Record<string, number> = {};

	parameters.forEach((param) => {
		const value =
			typeof param.defaultValue === 'boolean'
				? param.defaultValue
					? 1
					: 0
				: param.defaultValue;
		set(parameterValues, `${param.parameterRef}`, value);
	});
	return parameterValues;
}

const parser = new Parser({
	operators: {
		length: false, // Disable length operator to avoid conflicts with variable names
		assignment: true,
	},
});

parser.functions = {
	ceil: Math.ceil,
	floor: Math.floor,
	round: Math.round,
};

const getParameterIdsFromString = (content: string): string[] => {
	const parameterIds: string[] = [];
	const matches = content.match(REGEX_LINK_PARAMETER);
	if (matches) {
		matches.forEach((match) => {
			if (match.startsWith('parameter:')) {
				parameterIds.push(match);
			}
		});
	}
	return parameterIds;
};

const replaceParameterIdsWithText = (
	text: string,
	parameterRefs: {
		id: string;
		parameterRef: string;
	}[]
) => {
	if (!parameterRefs || parameterRefs.length === 0) {
		return text;
	}
	const regex = new RegExp(`(${parameterRefs.map((p) => p.id).join('|')})`, 'g');
	// Create a map of parameter IDs to their text.
	const parameterRefRecord: { [key: string]: string } = {};
	parameterRefs.forEach((parameterRef) => {
		parameterRefRecord[parameterRef.id] = parameterRef.parameterRef;
	});
	const replaced = text.replaceAll(regex, (match) => {
		return parameterRefRecord[match] || match;
	});
	return replaced;
};

const extractVariablesFromFormula = (
	formula: string,
	parameterValues?: Record<string, Record<string, number>>
) => {
	try {
		if (parameterValues) {
			return parser.parse(formula).simplify(parameterValues).variables({ withMembers: true });
		}
		const variablesList = parser.parse(formula).variables({
			withMembers: true,
		});

		return variablesList;
	} catch {
		return { error: 'Formula is invalid' };
	}
};

const validateSimpleFormula = (
	formula: string,
	parameterValues: Record<string, Record<string, number>>
):
	| string[]
	| {
			error: string;
			meta: {
				missingVariables: string[];
			};
	  } => {
	const variables = extractVariablesFromFormula(formula);

	if ('error' in variables) {
		return {
			error: 'Formula is invalid',
			meta: {
				missingVariables: [],
			},
		};
	}

	// A sample binding for the projection token, so authoring validation can
	// save a paved-road formula: the real function is canvas-bound at
	// evaluation time and cannot exist here. The stub returns a list, matching
	// the real function's multi-material contract.
	//
	// Self-gating, so it needs no `isProjectionFormula` check: a formula that
	// resolves this name necessarily contains the token, which is exactly what
	// makes it a projection formula and earns it the real binding at runtime.
	// Seeding it for a formula that never mentions it is an unread key.
	const seededValues = {
		...parameterValues,
		packProjectedLineSegments: () => [],
	} as unknown as Record<string, Record<string, number>>;

	const missingVariables = extractVariablesFromFormula(formula, seededValues);

	if ('error' in missingVariables) {
		return {
			error: 'Formula is invalid',
			meta: {
				missingVariables: [],
			},
		};
	}

	try {
		const res = parser.evaluate(formula, seededValues);

		// A list result is the multi-material allocation; every element must be
		// numeric. NaN elements are the unservable signal, which is a LIVE
		// validation concern, not an authoring error.
		//
		// ! Scalars are checked by TYPE before finiteness. `isNaN` coerces, so
		// it passed anything `Number()` could read — expr-eval evaluates `[]+[]`
		// to 0, which `isNaN` called valid. `isFinite` matches what
		// `evaluateFormula` enforces on the same result at runtime.
		const invalid = Array.isArray(res)
			? res.some((quantity) => typeof quantity !== 'number')
			: typeof res !== 'number' || !isFinite(res);

		if (invalid) {
			return { error: 'Formula is invalid', meta: { missingVariables } };
		}
	} catch (e) {
		console.error('Error evaluating formula:', e);
		//showToast('error', 'Formula is invalid');
		return { error: 'Formula is invalid', meta: { missingVariables } };
	}

	return variables.filter((v) => !missingVariables.includes(v));
};

/**
 * Evaluates a formula with given parameter values
 * @param formula The formula string to evaluate
 * @param parameterValues Record of parameter values organized by category
 * @returns The evaluated result
 */
function evaluateFormula(
	formula: string,
	parameterValues: Record<string, Record<string, number>>
): number | number[] {
	const result = parser.evaluate(formula, parameterValues);

	// A list is a multi-material allocation — one BOM line per selected
	// material, index-aligned. Sanitize per element: the scalar guard below
	// would flatten a multi-element list to 0 and coerce a single-element one
	// into a number (see the seam pins in studProjectionFormula.spec.ts).
	if (Array.isArray(result)) {
		return result.map((quantity) =>
			typeof quantity === 'number' && isFinite(quantity) ? quantity : 0
		);
	}

	if (!isFinite(result)) {
		return 0;
	}
	return result;
}

export function getTakeOffLevelFromTakeOffId(
	id: types.wholeHouse.takeOff.Id
): types.ai.floorLevel.Id {
	// Determine the level based on the id
	let level: types.ai.floorLevel.Id;

	switch (id) {
		// Basement
		case 'wholeHouseFoundationLevel0':
		case 'wholeHouseFoundationBasementLevel0':
		case 'wholeHouseFramingLevel0':
		case 'wholeHouseIntFramingLevel0':
		case 'wholeHouseExtFramingLevel0':
		case 'wholeHouseFlooringLevel0':
		case 'wholeHouseWindowsLevel0':
		case 'wholeHouseDoorsLevel0':
		case 'wholeHouseCeilingsLevel0':
		case 'wholeHouseInsulationLevel0':
		case 'wholeHouseDrywallLevel0':
		case 'wholeHouseMillworkLevel0':
		case 'wholeHouseInteriorFinishesLevel0':
		case 'wholeHouseExteriorFinishesLevel0':
		case 'wholeHouseIntWallCladdingLevel0':
		case 'wholeHouseExtWallCladdingLevel0':
		case 'wholeHouseIntFixturesLevel0':
			// level = 'Basement';
			level = 'level0';
			break;

		// Level 1
		case 'wholeHouseFoundationLevel1':
		case 'wholeHouseFoundationBasementLevel1':
		case 'wholeHouseFramingLevel1':
		case 'wholeHouseIntFramingLevel1':
		case 'wholeHouseExtFramingLevel1':
		case 'wholeHouseFlooringLevel1':
		case 'wholeHouseDecksAndPorchesLevel1':
		case 'wholeHouseWindowsLevel1':
		case 'wholeHouseDoorsLevel1':
		case 'wholeHouseCeilingsLevel1':
		case 'wholeHouseInsulationLevel1':
		case 'wholeHouseDrywallLevel1':
		case 'wholeHouseMillworkLevel1':
		case 'wholeHouseInteriorFinishesLevel1':
		case 'wholeHouseExteriorFinishesLevel1':
		case 'wholeHouseIntWallCladdingLevel1':
		case 'wholeHouseExtWallCladdingLevel1':
		case 'wholeHouseIntFixturesLevel1':
			level = 'level1';
			break;

		// Level 2
		case 'wholeHouseFoundationLevel2':
		case 'wholeHouseFoundationBasementLevel2':
		case 'wholeHouseFramingLevel2':
		case 'wholeHouseIntFramingLevel2':
		case 'wholeHouseExtFramingLevel2':
		case 'wholeHouseFlooringLevel2':
		case 'wholeHouseDecksAndPorchesLevel2':
		case 'wholeHouseWindowsLevel2':
		case 'wholeHouseDoorsLevel2':
		case 'wholeHouseCeilingsLevel2':
		case 'wholeHouseInsulationLevel2':
		case 'wholeHouseDrywallLevel2':
		case 'wholeHouseMillworkLevel2':
		case 'wholeHouseInteriorFinishesLevel2':
		case 'wholeHouseExteriorFinishesLevel2':
		case 'wholeHouseIntWallCladdingLevel2':
		case 'wholeHouseExtWallCladdingLevel2':
		case 'wholeHouseIntFixturesLevel2':
			level = 'level2';
			break;

		// Level 3
		case 'wholeHouseFoundationLevel3':
		case 'wholeHouseFoundationBasementLevel3':
		case 'wholeHouseFramingLevel3':
		case 'wholeHouseIntFramingLevel3':
		case 'wholeHouseExtFramingLevel3':
		case 'wholeHouseFlooringLevel3':
		case 'wholeHouseDecksAndPorchesLevel3':
		case 'wholeHouseWindowsLevel3':
		case 'wholeHouseDoorsLevel3':
		case 'wholeHouseCeilingsLevel3':
		case 'wholeHouseInsulationLevel3':
		case 'wholeHouseDrywallLevel3':
		case 'wholeHouseMillworkLevel3':
		case 'wholeHouseInteriorFinishesLevel3':
		case 'wholeHouseExteriorFinishesLevel3':
		case 'wholeHouseIntWallCladdingLevel3':
		case 'wholeHouseExtWallCladdingLevel3':
		case 'wholeHouseIntFixturesLevel3':
			level = 'level3';
			break;

		// Level 4
		case 'wholeHouseFoundationLevel4':
		case 'wholeHouseFoundationBasementLevel4':
		case 'wholeHouseFramingLevel4':
		case 'wholeHouseIntFramingLevel4':
		case 'wholeHouseExtFramingLevel4':
		case 'wholeHouseFlooringLevel4':
		case 'wholeHouseDecksAndPorchesLevel4':
		case 'wholeHouseWindowsLevel4':
		case 'wholeHouseDoorsLevel4':
		case 'wholeHouseCeilingsLevel4':
		case 'wholeHouseInsulationLevel4':
		case 'wholeHouseDrywallLevel4':
		case 'wholeHouseMillworkLevel4':
		case 'wholeHouseInteriorFinishesLevel4':
		case 'wholeHouseExteriorFinishesLevel4':
		case 'wholeHouseIntWallCladdingLevel4':
		case 'wholeHouseExtWallCladdingLevel4':
		case 'wholeHouseIntFixturesLevel4':
			level = 'level4';
			break;

		// Level 5
		case 'wholeHouseFoundationLevel5':
		case 'wholeHouseFoundationBasementLevel5':
		case 'wholeHouseFramingLevel5':
		case 'wholeHouseIntFramingLevel5':
		case 'wholeHouseExtFramingLevel5':
		case 'wholeHouseFlooringLevel5':
		case 'wholeHouseDecksAndPorchesLevel5':
		case 'wholeHouseWindowsLevel5':
		case 'wholeHouseDoorsLevel5':
		case 'wholeHouseCeilingsLevel5':
		case 'wholeHouseInsulationLevel5':
		case 'wholeHouseDrywallLevel5':
		case 'wholeHouseMillworkLevel5':
		case 'wholeHouseInteriorFinishesLevel5':
		case 'wholeHouseExteriorFinishesLevel5':
		case 'wholeHouseIntWallCladdingLevel5':
		case 'wholeHouseExtWallCladdingLevel5':
		case 'wholeHouseIntFixturesLevel5':
			level = 'level5';
			break;

		// Level 6
		case 'wholeHouseFoundationLevel6':
		case 'wholeHouseFoundationBasementLevel6':
		case 'wholeHouseFramingLevel6':
		case 'wholeHouseIntFramingLevel6':
		case 'wholeHouseExtFramingLevel6':
		case 'wholeHouseFlooringLevel6':
		case 'wholeHouseDecksAndPorchesLevel6':
		case 'wholeHouseWindowsLevel6':
		case 'wholeHouseDoorsLevel6':
		case 'wholeHouseCeilingsLevel6':
		case 'wholeHouseInsulationLevel6':
		case 'wholeHouseDrywallLevel6':
		case 'wholeHouseMillworkLevel6':
		case 'wholeHouseInteriorFinishesLevel6':
		case 'wholeHouseExteriorFinishesLevel6':
		case 'wholeHouseIntWallCladdingLevel6':
		case 'wholeHouseExtWallCladdingLevel6':
		case 'wholeHouseIntFixturesLevel6':
			level = 'level6';
			break;

		// Level 7
		case 'wholeHouseFoundationLevel7':
		case 'wholeHouseFoundationBasementLevel7':
		case 'wholeHouseFramingLevel7':
		case 'wholeHouseIntFramingLevel7':
		case 'wholeHouseExtFramingLevel7':
		case 'wholeHouseFlooringLevel7':
		case 'wholeHouseDecksAndPorchesLevel7':
		case 'wholeHouseWindowsLevel7':
		case 'wholeHouseDoorsLevel7':
		case 'wholeHouseCeilingsLevel7':
		case 'wholeHouseInsulationLevel7':
		case 'wholeHouseDrywallLevel7':
		case 'wholeHouseMillworkLevel7':
		case 'wholeHouseInteriorFinishesLevel7':
		case 'wholeHouseExteriorFinishesLevel7':
		case 'wholeHouseIntWallCladdingLevel7':
		case 'wholeHouseExtWallCladdingLevel7':
		case 'wholeHouseIntFixturesLevel7':
			level = 'level7';
			break;

		// Not Applicable
		case 'wholeHouseBlockWork':
		case 'wholeHouseFramingMaterial':
		case 'wholeHouseRoofing':
		case 'wholeHouseEavesSoffits':
		case 'wholeHouseGutters':
		case 'wholeHouseFireRatedSystems':
		case 'wholeHouseHardware':
		case 'wholeHouseDimensionalFloorRoofLayout':
		case 'wholeHouseEwpFloorRoofLayout':
		case 'wholeHouseFlatRoofing':
		case 'wholeHouseOpenings':
			level = 'notApplicable';
			break;

		default:
			return assertUnreachable(id, 'wohUtils.getTakeOffLevelFromTakeOffId');
	}

	return level;
}

function getTakeOffTypeFromTakeOffId(
	id: types.wholeHouse.takeOff.Id
): types.job.ancillaryItem.WholeHouseId {
	switch (id) {
		// Foundation
		case 'wholeHouseFoundationLevel0':
		case 'wholeHouseFoundationLevel1':
		case 'wholeHouseFoundationLevel2':
		case 'wholeHouseFoundationLevel3':
		case 'wholeHouseFoundationLevel4':
		case 'wholeHouseFoundationLevel5':
		case 'wholeHouseFoundationLevel6':
		case 'wholeHouseFoundationLevel7':
			return 'wholeHouseFoundation';

		// Foundation with Basement
		case 'wholeHouseFoundationBasementLevel0':
		case 'wholeHouseFoundationBasementLevel1':
		case 'wholeHouseFoundationBasementLevel2':
		case 'wholeHouseFoundationBasementLevel3':
		case 'wholeHouseFoundationBasementLevel4':
		case 'wholeHouseFoundationBasementLevel5':
		case 'wholeHouseFoundationBasementLevel6':
		case 'wholeHouseFoundationBasementLevel7':
			return 'wholeHouseFoundationBasement';

		// Framing
		case 'wholeHouseFramingLevel0':
		case 'wholeHouseFramingLevel1':
		case 'wholeHouseFramingLevel2':
		case 'wholeHouseFramingLevel3':
		case 'wholeHouseFramingLevel4':
		case 'wholeHouseFramingLevel5':
		case 'wholeHouseFramingLevel6':
		case 'wholeHouseFramingLevel7':
			return 'wholeHouseFraming';

		// Internal Framing
		case 'wholeHouseIntFramingLevel0':
		case 'wholeHouseIntFramingLevel1':
		case 'wholeHouseIntFramingLevel2':
		case 'wholeHouseIntFramingLevel3':
		case 'wholeHouseIntFramingLevel4':
		case 'wholeHouseIntFramingLevel5':
		case 'wholeHouseIntFramingLevel6':
		case 'wholeHouseIntFramingLevel7':
			return 'wholeHouseIntFraming';

		// External Framing
		case 'wholeHouseExtFramingLevel0':
		case 'wholeHouseExtFramingLevel1':
		case 'wholeHouseExtFramingLevel2':
		case 'wholeHouseExtFramingLevel3':
		case 'wholeHouseExtFramingLevel4':
		case 'wholeHouseExtFramingLevel5':
		case 'wholeHouseExtFramingLevel6':
		case 'wholeHouseExtFramingLevel7':
			return 'wholeHouseExtFraming';

		// Flooring
		case 'wholeHouseFlooringLevel0':
		case 'wholeHouseFlooringLevel1':
		case 'wholeHouseFlooringLevel2':
		case 'wholeHouseFlooringLevel3':
		case 'wholeHouseFlooringLevel4':
		case 'wholeHouseFlooringLevel5':
		case 'wholeHouseFlooringLevel6':
		case 'wholeHouseFlooringLevel7':
			return 'wholeHouseFlooring';

		// Decks & Porches
		case 'wholeHouseDecksAndPorchesLevel1':
		case 'wholeHouseDecksAndPorchesLevel2':
		case 'wholeHouseDecksAndPorchesLevel3':
		case 'wholeHouseDecksAndPorchesLevel4':
		case 'wholeHouseDecksAndPorchesLevel5':
		case 'wholeHouseDecksAndPorchesLevel6':
		case 'wholeHouseDecksAndPorchesLevel7':
			return 'wholeHouseDecksAndPorches';

		// Windows
		case 'wholeHouseWindowsLevel0':
		case 'wholeHouseWindowsLevel1':
		case 'wholeHouseWindowsLevel2':
		case 'wholeHouseWindowsLevel3':
		case 'wholeHouseWindowsLevel4':
		case 'wholeHouseWindowsLevel5':
		case 'wholeHouseWindowsLevel6':
		case 'wholeHouseWindowsLevel7':
			return 'wholeHouseWindows';

		// Doors
		case 'wholeHouseDoorsLevel0':
		case 'wholeHouseDoorsLevel1':
		case 'wholeHouseDoorsLevel2':
		case 'wholeHouseDoorsLevel3':
		case 'wholeHouseDoorsLevel4':
		case 'wholeHouseDoorsLevel5':
		case 'wholeHouseDoorsLevel6':
		case 'wholeHouseDoorsLevel7':
			return 'wholeHouseDoors';

		// Interior Ceilings
		case 'wholeHouseCeilingsLevel0':
		case 'wholeHouseCeilingsLevel1':
		case 'wholeHouseCeilingsLevel2':
		case 'wholeHouseCeilingsLevel3':
		case 'wholeHouseCeilingsLevel4':
		case 'wholeHouseCeilingsLevel5':
		case 'wholeHouseCeilingsLevel6':
		case 'wholeHouseCeilingsLevel7':
			return 'wholeHouseCeilings';

		// Insulation
		case 'wholeHouseInsulationLevel0':
		case 'wholeHouseInsulationLevel1':
		case 'wholeHouseInsulationLevel2':
		case 'wholeHouseInsulationLevel3':
		case 'wholeHouseInsulationLevel4':
		case 'wholeHouseInsulationLevel5':
		case 'wholeHouseInsulationLevel6':
		case 'wholeHouseInsulationLevel7':
			return 'wholeHouseInsulation';

		// Drywall
		case 'wholeHouseDrywallLevel0':
		case 'wholeHouseDrywallLevel1':
		case 'wholeHouseDrywallLevel2':
		case 'wholeHouseDrywallLevel3':
		case 'wholeHouseDrywallLevel4':
		case 'wholeHouseDrywallLevel5':
		case 'wholeHouseDrywallLevel6':
		case 'wholeHouseDrywallLevel7':
			return 'wholeHouseDrywall';

		// Millwork
		case 'wholeHouseMillworkLevel0':
		case 'wholeHouseMillworkLevel1':
		case 'wholeHouseMillworkLevel2':
		case 'wholeHouseMillworkLevel3':
		case 'wholeHouseMillworkLevel4':
		case 'wholeHouseMillworkLevel5':
		case 'wholeHouseMillworkLevel6':
		case 'wholeHouseMillworkLevel7':
			return 'wholeHouseMillwork';

		// Interior Finishes
		case 'wholeHouseInteriorFinishesLevel0':
		case 'wholeHouseInteriorFinishesLevel1':
		case 'wholeHouseInteriorFinishesLevel2':
		case 'wholeHouseInteriorFinishesLevel3':
		case 'wholeHouseInteriorFinishesLevel4':
		case 'wholeHouseInteriorFinishesLevel5':
		case 'wholeHouseInteriorFinishesLevel6':
		case 'wholeHouseInteriorFinishesLevel7':
			return 'wholeHouseInteriorFinishes';

		// Exterior Finishes
		case 'wholeHouseExteriorFinishesLevel0':
		case 'wholeHouseExteriorFinishesLevel1':
		case 'wholeHouseExteriorFinishesLevel2':
		case 'wholeHouseExteriorFinishesLevel3':
		case 'wholeHouseExteriorFinishesLevel4':
		case 'wholeHouseExteriorFinishesLevel5':
		case 'wholeHouseExteriorFinishesLevel6':
		case 'wholeHouseExteriorFinishesLevel7':
			return 'wholeHouseExteriorFinishes';

		// Interior Walls
		case 'wholeHouseIntWallCladdingLevel0':
		case 'wholeHouseIntWallCladdingLevel1':
		case 'wholeHouseIntWallCladdingLevel2':
		case 'wholeHouseIntWallCladdingLevel3':
		case 'wholeHouseIntWallCladdingLevel4':
		case 'wholeHouseIntWallCladdingLevel5':
		case 'wholeHouseIntWallCladdingLevel6':
		case 'wholeHouseIntWallCladdingLevel7':
			return 'wholeHouseIntWallCladding';

		// Exterior Walls
		case 'wholeHouseExtWallCladdingLevel0':
		case 'wholeHouseExtWallCladdingLevel1':
		case 'wholeHouseExtWallCladdingLevel2':
		case 'wholeHouseExtWallCladdingLevel3':
		case 'wholeHouseExtWallCladdingLevel4':
		case 'wholeHouseExtWallCladdingLevel5':
		case 'wholeHouseExtWallCladdingLevel6':
		case 'wholeHouseExtWallCladdingLevel7':
			return 'wholeHouseExtWallCladding';

		// Miscellaneous
		case 'wholeHouseIntFixturesLevel0':
		case 'wholeHouseIntFixturesLevel1':
		case 'wholeHouseIntFixturesLevel2':
		case 'wholeHouseIntFixturesLevel3':
		case 'wholeHouseIntFixturesLevel4':
		case 'wholeHouseIntFixturesLevel5':
		case 'wholeHouseIntFixturesLevel6':
		case 'wholeHouseIntFixturesLevel7':
			return 'wholeHouseIntFixtures';

		case 'wholeHouseBlockWork':
		case 'wholeHouseFramingMaterial':
		case 'wholeHouseRoofing':
		case 'wholeHouseEavesSoffits':
		case 'wholeHouseGutters':
		case 'wholeHouseFireRatedSystems':
		case 'wholeHouseHardware':
		case 'wholeHouseDimensionalFloorRoofLayout':
		case 'wholeHouseEwpFloorRoofLayout':
		case 'wholeHouseFlatRoofing':
		case 'wholeHouseOpenings':
			return id;

		default:
			return assertUnreachable(id, 'wohUtils.getTakeOffTypeFromTakeOffId');
	}
}

const getTakeOffIdFromTakeOffTypeAndLevel = (
	type: types.job.ancillaryItem.WholeHouseId,
	level: types.ai.floorLevel.Id
): types.wholeHouse.takeOff.Id => {
	// Prefer exact match where both type and level align by consulting the canonical ids
	const res = constants.wholeHouse.takeOff.ids.find((id) => {
		if (
			getTakeOffTypeFromTakeOffId(id) === type &&
			getTakeOffLevelFromTakeOffId(id) === level
		) {
			return id;
		}
		return null;
	});

	if (!res) {
		throw new Error(`No takeoff id found for type ${type} and level ${level}`);
	}
	return res;
};

type FormulaOption = {
	id: string;
	name: string;
	value: number;
};
const transformFormulaText = (props: {
	formula: string;
	isRandomMaterial?: boolean;
	materialPropertyOptions?: FormulaOption[];
	isRandomMeasurement?: boolean;
	measurementPropertyOptions?: FormulaOption[];
	isRandomParameter?: boolean;
	parameterOptions?: FormulaOption[];
	isRandomDeduction?: boolean;
	deductionPropertyOptions?: FormulaOption[];
}):
	| {
			error: {
				message: string;
				missingVariables?: string[];
			};
	  }
	| {
			simplifiedFormula: string;
	  } => {
	// Check if the formula is empty
	if (!props.formula || props.formula.trim() === '') {
		return {
			error: {
				message: 'Formula is empty',
			},
		};
	}
	const formula = props.formula;
	const parameterOptions = props.parameterOptions ?? [];
	const materialPropertyOptions = props.materialPropertyOptions ?? [];
	const measurementPropertyOptions = props.measurementPropertyOptions ?? [];
	const deductionPropertyOptions = props.deductionPropertyOptions ?? [];

	// Transform the formula by replacing parameter IDs with names
	let currentFormulaText = formula;
	const parameterIds = getParameterIdsFromString(currentFormulaText);
	// Replace parameter IDs with names
	if (parameterIds && parameterIds.length > 0) {
		if (props.isRandomParameter) {
			// create random parameter info from the parameterIds
			parameterOptions.concat(
				parameterIds.map((id, index) => ({
					id,
					name: `parameter.random${index}`,
					value: index + 1,
				}))
			);
		}
		if (!parameterOptions) {
			return {
				error: {
					message: `Parameter options are not available required ${parameterIds.join(',')}.`,
					missingVariables: parameterIds,
				},
			}; // Return original if data not ready
		}
		const filteredParameterOptions = parameterOptions
			.filter((option) => parameterIds.includes(option.id))
			.map((option) => ({
				id: option.id,
				parameterRef: option.name,
			}));

		const parameterRegex = new RegExp(
			filteredParameterOptions?.map((option) => option.id).join('|'),
			'g'
		);
		// Replace parameter IDs with names
		currentFormulaText = currentFormulaText.replaceAll(parameterRegex, (match) => {
			const parameter = filteredParameterOptions.find((option) => option.id === match);
			return parameter ? parameter.parameterRef : match;
		});
	}

	// Replace material properties
	if (materialPropertyOptions) {
		if (props.isRandomMaterial) {
			// replace all material: with material.
			currentFormulaText = currentFormulaText.replaceAll('material:', 'material.');
		} else if (materialPropertyOptions.length > 0) {
			const materialPropertyRegex = new RegExp(
				materialPropertyOptions?.map((option) => option.name).join('|'),
				'g'
			);

			currentFormulaText = currentFormulaText.replaceAll(materialPropertyRegex, (match) => {
				const materialProperty = materialPropertyOptions?.find(
					(option) => option.id === match
				);
				return materialProperty ? materialProperty.name : match;
			});
		}
	}
	// Replace measurement properties
	if (props.isRandomMeasurement) {
		// replace all measurement: with measurement.
		currentFormulaText = currentFormulaText.replaceAll('measurement:', 'measurement.');
	} else if (measurementPropertyOptions.length > 0) {
		const measurementPropertyRegex = new RegExp(
			measurementPropertyOptions?.map((option) => option.name).join('|'),
			'g'
		);
		currentFormulaText = currentFormulaText.replaceAll(measurementPropertyRegex, (match) => {
			const measurementProperty = measurementPropertyOptions?.find(
				(option) => option.id === match
			);
			return measurementProperty ? measurementProperty.name : match;
		});
	}

	// Replace deduction properties
	if (props.isRandomDeduction) {
		// replace all deduction: with deduction.
		currentFormulaText = currentFormulaText.replaceAll('deduction:', 'deduction.');
	} else if (deductionPropertyOptions.length > 0) {
		const deductionPropertyRegex = new RegExp(
			deductionPropertyOptions?.map((option) => option.name).join('|'),
			'g'
		);
		currentFormulaText = currentFormulaText.replaceAll(deductionPropertyRegex, (match) => {
			const deductionProperty = deductionPropertyOptions?.find(
				(option) => option.id === match
			);
			return deductionProperty ? deductionProperty.name : match;
		});
	}

	return {
		simplifiedFormula: currentFormulaText,
	};
};
const validateFormulaHelper = (props: {
	formula: string;
	parameterOptions: FormulaOption[];
	materialPropertyOptions: FormulaOption[];
	measurementPropertyOptions: FormulaOption[];
	deductionPropertyOptions: FormulaOption[];
	validate?: {
		material: boolean;
	};
}):
	| {
			isValid: true;
			simpleFormula: string;
			formula: string;
			propertyValues: Record<string, Record<string, number>>;
			formulaVariableIds: string[];
			formulaVariablesNames: string[];
			propertyOptions: {
				id: string;
				name: string;
				value: number;
			}[];
	  }
	| {
			isValid: false;
			error: {
				message: string;
				missingVariables: string[];
			};
	  } => {
	// Step 1: Convert dot-notation parameter refs (parameter.Name) to UUID format
	// (parameter:UUID) by matching against the available parameter options.
	// This handles legacy formulas saved in the old format so the user doesn't
	// have to manually re-enter each parameter.
	let normalizedFormula = props.formula.replace(/\bparameter\.([a-zA-Z0-9_]+)\b/g, (match) => {
		const option = props.parameterOptions.find((o) => o.name === match);
		return option ? option.id : match;
	});

	// Step 2: Fix corrupted parameter refs of the form parameter:UUID_suffix,
	// which occur when autocomplete inserts a UUID mid-word and leaves a trailing
	// text suffix (e.g. parameter:UUID_treated instead of parameter:UUID2 where
	// UUID2 is the "bottom_plate_treated" parameter). Reconstruct the intended
	// parameter name from the source parameter's name plus the suffix and look up
	// its UUID.
	const REGEX_UUID_WITH_SUFFIX =
		/(parameter:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})([a-zA-Z0-9_]+)/g;
	normalizedFormula = normalizedFormula.replace(
		REGEX_UUID_WITH_SUFFIX,
		(match, paramId: string, suffix: string) => {
			const sourceParam = props.parameterOptions.find((o) => o.id === paramId);
			if (!sourceParam) return match;
			// e.g. 'parameter.bottom_plate' + '_treated' = 'parameter.bottom_plate_treated'
			const reconstructedName = `${sourceParam.name}${suffix}`;
			const targetParam = props.parameterOptions.find((o) => o.name === reconstructedName);
			return targetParam ? targetParam.id : match;
		}
	);

	// Step 3: Convert dot-notation measurement refs (measurement.xxx) to colon
	// format (measurement:xxx) by matching against measurementPropertyOptions.
	normalizedFormula = normalizedFormula.replace(/\bmeasurement\.([a-zA-Z0-9_]+)\b/g, (match) => {
		const option = props.measurementPropertyOptions.find((o) => o.name === match);
		return option ? option.id : match;
	});

	// Step 4: Convert dot-notation material refs (material.xxx) to colon
	// format (material:xxx) by matching against materialPropertyOptions.
	normalizedFormula = normalizedFormula.replace(/\bmaterial\.([a-zA-Z0-9_]+)\b/g, (match) => {
		const option = props.materialPropertyOptions.find((o) => o.name === match);
		return option ? option.id : match;
	});

	// Step 5: Convert dot-notation deduction refs (deduction.xxx) to colon
	// format (deduction:xxx) by matching against deductionPropertyOptions.
	normalizedFormula = normalizedFormula.replace(/\bdeduction\.([a-zA-Z0-9_]+)\b/g, (match) => {
		const option = props.deductionPropertyOptions.find((o) => o.name === match);
		return option ? option.id : match;
	});

	const transformedFormula = transformFormulaText({
		...props,
		formula: normalizedFormula,
		isRandomMaterial: props.validate?.material ?? false,
	});
	if ('error' in transformedFormula) {
		return {
			isValid: false,
			error: {
				message: transformedFormula.error?.message,
				missingVariables: transformedFormula.error.missingVariables ?? [],
			},
		};
	}
	const currentFormulaText = transformedFormula.simplifiedFormula;
	// getVariable list from formula
	const varUsed = extractVariablesFromFormula(currentFormulaText);
	if ('error' in varUsed) {
		return {
			isValid: false,
			error: {
				message: varUsed.error,
				missingVariables: [],
			},
		};
	}

	const optionsUsed = [
		...(props.parameterOptions?.filter((option) =>
			varUsed.filter((v) => v.startsWith('parameter.')).includes(option.name)
		) ?? []),
		...(props.materialPropertyOptions?.filter((option) =>
			varUsed.filter((v) => v.startsWith('material.')).includes(option.name)
		) ?? []),
		...(props.measurementPropertyOptions?.filter((option) =>
			varUsed.filter((v) => v.startsWith('measurement.')).includes(option.name)
		) ?? []),
		...(props.deductionPropertyOptions?.filter((option) =>
			varUsed.filter((v) => v.startsWith('deduction.')).includes(option.name)
		) ?? []),
	];

	const propertyValues: Record<string, Record<string, number>> = {};
	for (const property of optionsUsed) {
		set(propertyValues, property.name, property.value);
	}

	const res = validateSimpleFormula(currentFormulaText, propertyValues);
	if ('error' in res) {
		console.error(`validateFormulaHelper.validateSimpleFormula ${currentFormulaText}`, {
			propertyValues,
			res,
		});
		return {
			isValid: false,
			error: {
				message: res.error,
				missingVariables: res.meta.missingVariables,
			},
		};
	}

	return {
		isValid: true,
		simpleFormula: currentFormulaText,
		formula: normalizedFormula,
		propertyValues,
		formulaVariableIds: optionsUsed.map((option) => option.id),
		formulaVariablesNames: optionsUsed.map((option) => option.name),
		propertyOptions: optionsUsed,
	};
};

const makeMaterialOption = (property: types.wholeHouse.formula.materialProperty.Id) => {
	return {
		id: `material:${property}`,
		name: `material.${property}`,
	};
};

const makeMeasurementOption = (property: types.wholeHouse.formula.measurementProperty.Id) => {
	return {
		id: `measurement:${property}`,
		name: `measurement.${property}`,
	};
};

const makeDeductionOption = (property: types.wholeHouse.formula.deductionProperty.Id) => {
	return {
		id: `deduction:${property}`,
		name: `deduction.${property}`,
	};
};

const makeParameterOption = (property: {
	id: string;
	name: string;
	parameterRef: string;
	value: number;
	parameterType: 'integer' | 'decimal' | 'boolean';
}) => {
	return {
		id: property.id,
		label: property.name,
		name: property.parameterRef,
		value: property.value,
		parameterType: property.parameterType,
	};
};

export function convertToBomObject(
	canvas: Pick<
		types.wholeHouse.canvas.DetailsItem,
		'layers' | 'scale' | 'recipes' | 'id' | 'measurementSystem'
	>,
	groupedBy: (keyof types.wholeHouse.metric.Quantity)[] = ['materialSku']
): Omit<types.wholeHouse.bom.Item, 'id' | 'groupId'>[] {
	const quantityList = calculateQuantityForCanvas(canvas, canvas.measurementSystem);
	if (!quantityList || quantityList.length === 0) {
		return [];
	}

	const groupedEntries = entries(
		groupBy(quantityList, (item) => groupedBy.map((key) => item[key]).join('|'))
	);

	const result = map(
		groupedEntries,
		([, items]): Omit<types.wholeHouse.bom.Item, 'id' | 'groupId'> | undefined => {
			const firstItem = items[0];

			if (!firstItem) {
				return undefined;
			}

			// By id, not by reference: `uniq` compares identity, so one usage reached by two
			// contributions would be listed twice.
			const usages = uniqBy(
				items
					.filter((item) => !item.isDeduction)
					.map((item) => ({
						id: item.usageId,
						name: item.usageName,
					})),
				(usage) => usage.id
			);

			/**
			 * One entry per contribution. The item total below is derived from these same
			 * entries rather than recomputed from `items`, so the two cannot coalesce a
			 * quantity differently: this list is what the layer-grouped preview sums
			 * (`bomLayerGroupingUtils`), and the total is what the flat view and the persisted
			 * BoM show. They used to disagree — `|| 0` here against `?? 0` in the totals,
			 * and `NaN || 0` is `0` where `NaN ?? 0` is `NaN` — so an item with one
			 * unevaluable input read 0 flat and its finite part grouped.
			 */
			const input = items.map((item) => {
				const calculated = item.materialQuantity || 0;

				return {
					canvasRecipeId: item.canvasRecipeId,
					name: item.recipeName,
					measurementType: item.canvasRecipe.measurementType,
					simplifiedFormula: item.formula,
					formulaValues: item.formulaValues,
					quantity: {
						calculated,
						withWastage: ingredientQuantityWithWastage({
							calculated,
							wastage: item.wastage,
							isDeduction: item.isDeduction,
							quantityOverride: item.quantityOverride,
						}),
					},
					/** The usage's wastage for this contribution, as a ratio. */
					wastage: item.wastage,
					isOverridden: item.quantityOverride !== undefined,
					// What the formula produced, kept so an override never hides the original.
					computedWithWastage: ingredientQuantityWithWastage({
						calculated,
						wastage: item.wastage,
						isDeduction: item.isDeduction,
					}),
					...(item.projection ? { projection: item.projection } : {}),
					...(item.segments ? { segments: item.segments } : {}),
					usageId: item.usageId,
					usageName: item.usageName,
					isDeduction: item.isDeduction,
					ingIndex: item.ingredientIndex,
					materialIndex: item.materialIndex,
					layersUsed: [
						...(item.layers.map((l) => ({
							id: l.id,
							name: l.name,
							isDeduction: item.isDeduction,
						})) || []),
						...(item.deductionLayers.map((l) => ({
							id: l.id,
							name: l.name,
							isDeduction: true,
						})) || []),
					],
				};
			});

			const drawingData: types.wholeHouse.bom.Item['drawingData'] = [
				{
					canvasId: canvas.id,
					measurementSystem: canvas.measurementSystem,
					scale: canvas.scale,
					input,
				},
			];

			const totalQuantity = sum(
				input
					.filter((entry) => !entry.isDeduction)
					.map((entry) => entry.quantity.withWastage)
			);
			const totalDeduction = sum(
				input
					.filter((entry) => entry.isDeduction)
					.map((entry) => entry.quantity.withWastage)
			);

			// Deductions can exceed what they apply to — a cut-out larger than the area holding
			// it, or several that overlap. The row is still material to deliver, so it floors at
			// zero rather than asking an estimator to order a negative quantity.
			const computedQuantity = Math.max(0, totalQuantity - totalDeduction);

			const computedQuantityRounded = computedQuantity;

			return {
				material: {
					id: firstItem.materialId,
					sku: firstItem.materialSku,
					name: firstItem.materialName,
				},
				quantity: {
					originalValue: computedQuantityRounded || 0,
				},
				priceUnitRef: firstItem.priceUnitRef,
				priceUnitItem: firstItem.priceUnit,
				usages: usages,
				disabled: false,
				drawingData,
			};
		}
	);

	return compact(result);
}

function formatMeasurementWithUnit(
	value: number,
	metric: 'length' | 'area',
	measurementSystem: types.ui.MeasurementSystem
): string {
	if (!value || value <= 0) return '';
	const v = Math.round(value * 100) / 100;
	if (metric === 'length') {
		return measurementSystem === 'metric' ? `${v.toFixed(2)} m` : `${v.toFixed(2)} ft`;
	}
	return measurementSystem === 'metric' ? `${v.toFixed(2)} m²` : `${v.toFixed(2)} ft²`;
}

/**
 * Unit suffix for a drawing/formula metric (`length`, `area`, ...), matching
 * the unit convention `getMeasurementValuesV2` already computes values in —
 * metres/feet for length-like metrics, m²/ft² for area, degrees for metric
 * pitch. Appends to an already-formatted number; does not convert it.
 */
function getMeasurementUnitSuffix(
	metric: string,
	measurementSystem: types.ui.MeasurementSystem
): string {
	switch (metric) {
		case 'length':
		case 'height':
			return measurementSystem === 'metric' ? ' m' : ' ft';
		case 'area':
			return measurementSystem === 'metric' ? ' m²' : ' ft²';
		case 'volume':
			return measurementSystem === 'metric' ? ' m³' : ' ft³';
		case 'pitch':
			return measurementSystem === 'metric' ? '°' : '';
		default:
			return '';
	}
}

function computeLayerMeasurement(
	layer: types.wholeHouse.canvas.layer.Path,
	effectiveScale: number,
	measurementSystem: types.ui.MeasurementSystem
): types.api.wholeHouse.takeoff.typeRendered.LayerMeasurement {
	const { totalLength, totalArea } = computeConvertedMeasurements([layer], effectiveScale);
	return {
		length: totalLength,
		area: totalArea,
		lengthWithUnit: formatMeasurementWithUnit(totalLength, 'length', measurementSystem),
		areaWithUnit: totalArea
			? formatMeasurementWithUnit(totalArea, 'area', measurementSystem)
			: undefined,
	};
}

export const wohUtils = {
	isPerSegmentMeasurement,
	perSegmentMeasurements,
	formulaIncompatibilities,
	isValidPitch,
	formulaIncompatibilityMessage,
	makeMaterialOption,
	makeMeasurementOption,
	makeDeductionOption,
	makeParameterOption,

	//
	getMaterialPropertiesUsedByFormula,
	getMaterialParameterValues,
	getAdditionalParameterValues,
	extractMeasurementValues,
	getParameterIdsFromString,
	replaceParameterIdsWithText,
	//

	transformFormulaText,
	validateSimpleFormula,
	validateFormulaHelper,
	extractVariablesFromFormula,
	evaluateFormula,
	getDrawingPropertiesByMeasurementType,
	calculateQuantityForCanvas,
	calculateQuantityFromLayers,
	collectCanvasProjections,
	getMeasurementValuesV2,
	//
	computeConvertedMeasurements,
	applyQuantityAdjustments,
	applyWastage,
	selectedIngredientsOf,
	ingredientQuantityWithWastage,

	//
	getTakeOffLevelFromTakeOffId,
	getTakeOffTypeFromTakeOffId,
	getTakeOffIdFromTakeOffTypeAndLevel,
	//
	formulaRegex: {
		REGEX_LINK_PARAMETER,
		REGEX_LINK_MATERIAL,
		REGEX_LINK_MEASUREMENT,
		REGEX_LINK_DEDUCTION,
	},

	convertToBomObject,
	computeLayerMeasurement,
	formatMeasurementWithUnit,
	getMeasurementUnitSuffix,
	isLayerMeasurableBy,
};
